import { useCallback } from "react"
import { useLocation } from "react-router-dom"

function stripFrom(pathWithSearch: string) {
  const u = new URL(pathWithSearch, window.location.origin)
  u.searchParams.delete("from")
  return u.pathname + (u.search || "")
}

function routeOnly(urlLike: string) {
  const u = new URL(urlLike, window.location.origin)
  const p = u.pathname.replace(/\/+$/, "") || "/"
  return p
}

export function useFromUrl() {
  const location = useLocation()

  const getFromUrl = useCallback((href: string) => {
    const target = new URL(href, window.location.origin)

    if (
      target.origin !== window.location.origin ||
      target.searchParams.has("from")
    ) {
      return target.pathname + target.search
    }

    const now = new URL(window.location.href)
    const nowNoFrom = new URL(now.href)
    nowNoFrom.searchParams.delete("from")

    const targetRoute = routeOnly(target.href)
    const nowRoute = routeOnly(now.href)

    if (targetRoute === nowRoute) {
      return target.pathname + target.search
    }

    let currentFrom = new URLSearchParams(window.location.search).get("from")

    let guard = 12
    while (currentFrom && guard-- > 0) {
      try {
        const u = new URL(currentFrom, now.origin)
        const r = routeOnly(u.href)
        if (r === targetRoute) {
          return target.pathname + target.search
        }
        currentFrom = u.searchParams.get("from")
      } catch {
        break
      }
    }

    const inner = new URL(nowNoFrom.href)
    const existingFrom = new URLSearchParams(window.location.search).get("from")
    if (existingFrom) inner.searchParams.set("from", existingFrom)

    const baseFrom = inner.pathname + (inner.search || "")
    target.searchParams.set("from", baseFrom)

    return target.pathname + target.search
  }, [])

  const current = stripFrom(location.pathname + location.search)
  const from = new URLSearchParams(location.search).get("from") || null

  return { getFromUrl, from, current }
}
